import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { getDownloadURL, ref, uploadString } from 'firebase/storage';
import { storage } from '../firebase';
import { api } from './api';

export type ImageUploadOptions = {
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
  onProgress?: (progress: number) => void;
};

export const uploadImageToFirebase = async (imageData: string | File, path: string) => {
  const storageRef = ref(storage, path);

  if (typeof imageData === 'string' && imageData.startsWith('data:')) {
    // Upload base64 image
    const base64Data = imageData.split(',')[1];
    await uploadString(storageRef, base64Data, 'base64', {
      contentType: 'image/jpeg'
    });
  } else if (imageData instanceof File) {
    // Upload file
    const reader = new FileReader();
    const base64Data = await new Promise<string>((resolve) => {
      reader.onload = (e) => {
        const result = e.target?.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(imageData);
    });

    await uploadString(storageRef, base64Data, 'base64', {
      contentType: imageData.type
    });
  }

  return getDownloadURL(storageRef);
};

export const takePhoto = async () => {
  const image = await Camera.getPhoto({
    quality: 90,
    allowEditing: true,
    resultType: CameraResultType.Base64,
    source: CameraSource.Camera
  });

  if (!image.base64String) {
    throw new Error('No image data');
  }

  return `data:image/jpeg;base64,${image.base64String}`;
};

export const selectPhoto = async () => {
  const image = await Camera.getPhoto({
    quality: 90,
    allowEditing: true,
    resultType: CameraResultType.Base64,
    source: CameraSource.Photos
  });

  if (!image.base64String) {
    throw new Error('No image data');
  }

  return `data:image/jpeg;base64,${image.base64String}`;
};

export const handleFileChange = async (event: any) => {
  const file = event.target.files?.[0];
  if (!file) {
    throw new Error('No file selected');
  }
  return file;
};

export const updateUserAvatar = async (imageData: string | File) => {
  const auth = await FirebaseAuthentication.getCurrentUser();
  if (!auth.user) {
    throw new Error('No authenticated user');
  }

  const path = `/users/avatars/${auth.user.uid}`;
  const downloadUrl = await uploadImageToFirebase(imageData, path);

  await FirebaseAuthentication.updateProfile({
    photoUrl: downloadUrl
  });

  // Update avatar in the backend
  await api.put(`/profile/${auth.user.uid}/avatar`, {
    image: downloadUrl
  });

  return downloadUrl;
};
